<template>
    <div class="bg-gray-50">
        <Banner :title="t('nav.products')" :subtitle="t('products.subtitle')" class="mb-0" />
    </div>

    <section class="product-categories py-16 bg-gray-100">
        <div class="container mx-auto px-4">
            <div class="section-title text-center mb-10">
                <h2 class="text-3xl font-bold text-[#005005] mb-2 border-b-4 border-[#005005] inline-block pb-2">{{ t('products.categories.title') }}</h2>
                <p class="text-lg text-[#8d6e63]">{{ t('products.categories.subtitle') }}</p>
            </div>
            <div class="category-tabs">
                <ul class="tab-nav flex border-b border-gray-300 mb-8">
                    <li v-for="tab in tabs" 
                        :key="tab.key" 
                        @click="currentTab = tab.key" 
                        :class="[
                            'px-6 py-4 cursor-pointer font-semibold relative transition-all duration-300',
                            currentTab === tab.key 
                                ? 'text-[#005005] after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-[#005005] after:transform after:scale-x-100' 
                                : 'text-gray-700 hover:text-[#005005] after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-[#005005] after:transform after:scale-x-0 hover:after:scale-x-100'
                        ]"
                    >
                        {{ tab.label[locale] }}
                    </li>
                </ul>
                <div class="tab-content">
                    <div class="tab-pane active">
                        <div class="product-grid grid grid-cols-3 gap-8">
                            <div v-for="item in filteredProducts" :key="item.id" class="product-item bg-white rounded-lg overflow-hidden shadow-lg transition-transform hover:transform hover:-translate-y-2">
                                <div class="product-image h-64 overflow-hidden">
                                    <img :src="item.img" :alt="item.title[locale]" class="w-full h-full object-cover transition-transform hover:scale-105" />
                                </div>
                                <div class="product-details p-6 text-center">
                                    <h3 class="text-xl font-bold text-[#005005] mb-2">{{ item.title[locale] }}</h3>
                                    <p class="text-gray-600 mb-4">{{ item.desc[locale] }}</p>
                                    <router-link :to="{ name: 'product-detail', params: { id: item.id }}" class="btn bg-blue-600 text-white px-5 py-2 rounded-md hover:bg-blue-700 transition-colors">{{ t('products.viewDetails') }}</router-link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <section class="product-advantages py-16 bg-gray-100">
        <div class="container mx-auto px-4">
            <div class="section-title text-center mb-10">
                <h2 class="text-3xl font-bold text-[#005005] mb-2 border-b-4 border-[#005005] inline-block pb-2">{{ t('products.advantages.title') }}</h2>
                <p class="text-lg text-[#8d6e63]">{{ t('products.advantages.subtitle') }}</p>
            </div>
            <div class="advantages-grid grid grid-cols-3 gap-8">
                <div v-for="adv in advantages" :key="adv.title[locale]" class="advantage-item bg-white p-6 rounded-lg shadow-lg transition-transform hover:transform hover:-translate-y-2">
                    <div class="advantage-icon text-4xl text-blue-600 mb-4">
                        <i :class="adv.icon"></i>
                    </div>
                    <h3 class="text-xl font-bold text-[#005005] mb-2">{{ adv.title[locale] }}</h3>
                    <p class="text-gray-600">{{ adv.desc[locale] }}</p>
                </div>
            </div>
        </div>
    </section>

    <section class="product-applications py-16 bg-gray-100">
        <div class="container mx-auto px-4">
            <div class="section-title text-center mb-10">
                <h2 class="text-3xl font-bold text-[#005005] mb-2 border-b-4 border-[#005005] inline-block pb-2">{{ t('products.applications.title') }}</h2>
                <p class="text-lg text-[#8d6e63]">{{ t('products.applications.subtitle') }}</p>
            </div>
            <div class="applications-slider grid grid-cols-3 gap-8">
                <div v-for="app in applications" :key="app.title[locale]" class="application-item bg-white rounded-lg overflow-hidden shadow-lg transition-transform hover:transform hover:-translate-y-2">
                    <div class="application-image h-64 overflow-hidden">
                        <img :src="app.img" :alt="app.title[locale]" class="w-full h-full object-cover transition-transform hover:scale-105" />
                    </div>
                    <div class="application-content p-6">
                        <h3 class="text-xl font-bold text-[#005005] mb-2">{{ app.title[locale] }}</h3>
                        <p class="text-gray-600">{{ app.desc[locale] }}</p>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <CTA
      :title="t('products.cta.title')"
      :description="t('products.cta.description')"
      :buttonText="t('products.cta.buttonText')"
      buttonLink="/contact"
      backgroundImage="/images/56.png"
    />  
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import Banner from '../components/Banner.vue'
import { tabs, products, advantages, applications } from '../data/products'
import CTA from '../components/CTA.vue'

const { t, locale } = useI18n()
const currentTab = ref('all')

// 处理hash变化
const handleHashChange = () => {
    const hash = window.location.hash.slice(1) // 移除#号
    if (hash && tabs.some(tab => tab.key === hash)) {
        currentTab.value = hash
    }
}

onMounted(() => {
    // 初始加载时检查hash
    handleHashChange()
    
    // 监听hash变化
    window.addEventListener('hashchange', handleHashChange)
    
    console.log('Products:', products)
    console.log('Tabs:', tabs)
    console.log('Advantages:', advantages)
    console.log('Applications:', applications)
    window.scrollTo({
        top: 0,
        behavior: 'smooth'
    })
})

// 组件卸载时移除事件监听
onUnmounted(() => {
    window.removeEventListener('hashchange', handleHashChange)
})

// 监听currentTab变化，更新URL hash
watch(currentTab, (newTab) => {
    if (newTab !== 'all') {
        window.location.hash = newTab
    } else {
        window.location.hash = ''
    }
})

const filteredProducts = computed(() => {
    if (currentTab.value === 'all') return products
    return products.filter(p => p.category === currentTab.value)
})
</script>

<style scoped>
</style> 